import { useMemo } from 'react'

export interface NavData {
  menu?: Array<{
    id: string
    slug: string
    label: string
  }>
  company_name?: string
  company_email?: string
  company_logo?: string
  social?: {
    instagram?: string
    facebook?: string
  }
}

/*
  The nav payload is emitted as a <script type="application/json" id="nav-data-json">
  rather than a data-payload attribute because platformOS's `escape` filter does
  not escape double quotes inside HTML attribute values, which silently breaks
  JSON containing URLs or any other string values.
*/
function readNavData(): NavData {
  if (typeof document === 'undefined') return {}

  // Primary: JSON payload in <script type="application/json" id="nav-data-json">
  const scriptEl = document.getElementById('nav-data-json')
  if (scriptEl?.textContent) {
    try {
      return JSON.parse(scriptEl.textContent) as NavData
    } catch {
      /* fall through to attribute fallback */
    }
  }

  // Legacy fallback: data-payload attribute on #nav-data (broken for strings
  // with double quotes, but kept for any older cached pages still in flight).
  const el = document.getElementById('nav-data')
  if (el) {
    try {
      const raw = el.getAttribute('data-payload') ?? '{}'
      return JSON.parse(raw) as NavData
    } catch {
      /* fall through */
    }
  }
  return {}
}

// Captured synchronously at module evaluation, before React mounts.
const CACHED_NAV_DATA = readNavData()

export function useNavData(): NavData {
  return useMemo(() => CACHED_NAV_DATA, [])
}

export function useTheme(): 'light' | 'dark' {
  if (typeof document === 'undefined') return 'light'
  const el = document.getElementById('nav-data')
  return el?.getAttribute('data-theme') === 'dark' ? 'dark' : 'light'
}
